import { invalidatePolicyViews } from "../../../../lib/policyQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent } from "react";

import { Alert } from "../../../../components/ui/Alert";
import { Button } from "../../../../components/ui/Button";
import { Card } from "../../../../components/ui/Card";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import { FormField } from "../../../../components/ui/FormField";
import { Glossary } from "../../../../components/ui/Glossary";
import { Input } from "../../../../components/ui/Input";
import { Select } from "../../../../components/ui/Select";
import { Textarea } from "../../../../components/ui/Textarea";
import { TimezoneAutocomplete } from "../../../../components/ui/TimezoneAutocomplete";
import { WeeklyScheduleEditor } from "../../../../components/ui/WeeklyScheduleEditor";
import {
  type WorkScheduleProfileDetail,
  type WorkScheduleWindow,
  schedulesApi,
} from "../../../../lib/api/policies";
import { t } from "../../../../lib/i18n";
import { useState } from "react";

import { WeekdayCopyControl } from "../components/WeekdayCopyControl";
import { type OverlapError } from "../utils/validateWindowOverlap";

interface ScheduleProfileTabProps {
  scheduleId?: string;
  isNew: boolean;
  detail: WorkScheduleProfileDetail | null;
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  tz: string;
  setTz: (v: string) => void;
  scheduleDirection: "active_during" | "inactive_during";
  setScheduleDirection: (v: "active_during" | "inactive_during") => void;
  isActive: boolean;
  setIsActive: (v: boolean) => void;
  onSubmit: () => void;
  submitting: boolean;
  errMsg: string | null;
  /** Pre-submit overlap errors surfaced from parent so they stay sticky across renders. */
  overlapErrors: OverlapError[];
}

export function ScheduleProfileTab({
  scheduleId,
  isNew,
  detail,
  name,
  setName,
  description,
  setDescription,
  tz,
  setTz,
  scheduleDirection,
  setScheduleDirection,
  isActive,
  setIsActive,
  onSubmit,
  submitting,
  errMsg,
  overlapErrors,
}: ScheduleProfileTabProps) {
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="stack">
      <Card title={t("schedules.editor.detailsCardTitle")}>
        <form
          onSubmit={handleFormSubmit}
          style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}
        >
          {errMsg && (
            <div style={{ gridColumn: "1 / -1" }}>
              <Alert tone="danger">{errMsg}</Alert>
            </div>
          )}
          {overlapErrors.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <Alert tone="danger">
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {t("schedules.validation.overlap.title")}
                </div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {overlapErrors.slice(0, 3).map((err, idx) => (
                    <li key={idx} style={{ fontSize: 13 }}>
                      {formatOverlap(err)}
                    </li>
                  ))}
                </ul>
              </Alert>
            </div>
          )}
          <FormField label={t("schedules.editor.field.name")} required>
            {(fid) => (
              <Input
                id={fid}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t("schedules.editor.field.timezone")}>
            {(fid) => <TimezoneAutocomplete id={fid} value={tz} onChange={setTz} />}
          </FormField>
          <FormField
            label={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {t("schedules.editor.field.direction")}
                <Glossary termKey="workSchedule" />
              </span>
            }
            hint={
              scheduleDirection === "inactive_during"
                ? t("schedules.editor.directionHintInactive")
                : t("schedules.editor.directionHintActive")
            }
          >
            {(fid) => (
              <Select
                id={fid}
                value={scheduleDirection}
                onChange={(e) =>
                  setScheduleDirection(e.target.value as "active_during" | "inactive_during")
                }
              >
                <option value="active_during">
                  {t("schedules.editor.direction.activeDuring")}
                </option>
                <option value="inactive_during">
                  {t("schedules.editor.direction.inactiveDuring")}
                </option>
              </Select>
            )}
          </FormField>
          <div style={{ gridColumn: "1 / -1" }}>
            <Alert tone="info">
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {t("schedules.editor.activationMovedTitle")}
              </div>
              <div style={{ fontSize: 13 }}>
                {t("schedules.editor.activationMovedBody")}
              </div>
            </Alert>
          </div>
          <FormField label={t("common.status")}>
            {(fid) => (
              <Select
                id={fid}
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
              >
                <option value="true">{t("common.active")}</option>
                <option value="false">{t("common.inactive")}</option>
              </Select>
            )}
          </FormField>
          <FormField label={t("schedules.editor.field.description")}>
            {(fid) => (
              <Textarea
                id={fid}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            )}
          </FormField>
          <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button type="submit" loading={submitting}>
              {isNew ? t("schedules.editor.submitCreate") : t("common.save")}
            </Button>
          </div>
        </form>
      </Card>

      {!isNew && detail && scheduleId && (
        <ScheduleWindowsEditor
          scheduleId={scheduleId}
          windows={detail.windows}
          profileTimezone={tz || detail.timezone}
        />
      )}
    </div>
  );
}

function formatOverlap(err: OverlapError): string {
  return `${weekdayShort(err.weekday)} · ${fmt(err.firstStart)}–${fmt(err.firstEnd)} ↔ ${fmt(err.secondStart)}–${fmt(err.secondEnd)}`;
}

function weekdayShort(wd: number): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(
    new Date(2024, 0, wd + 1),
  );
}

function fmt(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

interface WindowsEditorProps {
  scheduleId: string;
  windows: WorkScheduleWindow[];
  profileTimezone?: string;
}

function ScheduleWindowsEditor({ scheduleId, windows, profileTimezone }: WindowsEditorProps) {
  const qc = useQueryClient();
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (vars: { weekday: number; start_minute: number; end_minute: number }) =>
      schedulesApi.createWindow(scheduleId, vars),
    onSuccess: () => {
      void invalidatePolicyViews(qc);
    },
  });

  const remove = useMutation({
    mutationFn: (windowId: string) => schedulesApi.deleteWindow(scheduleId, windowId),
    onSuccess: () => {
      setPendingRemove(null);
      void invalidatePolicyViews(qc);
    },
  });

  const copy = useMutation({
    mutationFn: async ({ sourceWeekday, targetWeekdays }: { sourceWeekday: number; targetWeekdays: number[] }) => {
      const sourceWindows = windows.filter((w) => w.weekday === sourceWeekday);
      for (const target of new Set(targetWeekdays)) {
        const existing = windows.filter((w) => w.weekday === target);
        for (const w of sourceWindows) {
          if (existing.some((e) => w.start_minute < e.end_minute && e.start_minute < w.end_minute)) continue;
          await schedulesApi.createWindow(scheduleId, {
            weekday: target,
            start_minute: w.start_minute,
            end_minute: w.end_minute,
            start_notification_title: w.start_notification_title,
            start_notification_body: w.start_notification_body,
            end_notification_title: w.end_notification_title,
            end_notification_body: w.end_notification_body,
          });
          existing.push({ ...w, weekday: target });
        }
      }
    },
    // Some windows may already have saved if a later request fails.
    onSettled: () => invalidatePolicyViews(qc),
  });
  const mutationError = create.error || remove.error || copy.error;
  const errMsg = mutationError instanceof Error ? mutationError.message : null;
  const populatedWeekdays = Array.from(new Set(windows.map((w) => w.weekday))).sort(
    (a, b) => a - b,
  );

  const handleCopy = (sourceWeekday: number, targetWeekdays: number[]) => {
    if (!copy.isPending) copy.mutate({ sourceWeekday, targetWeekdays });
  };

  return (
    <Card title={t("schedules.windows.cardTitle")}>
      {errMsg && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="danger">{errMsg}</Alert>
        </div>
      )}

      {windows.length === 0 && (
        <Alert tone="info">{t("schedules.windows.empty")}</Alert>
      )}

      <WeekdayCopyControl
        populatedWeekdays={populatedWeekdays}
        onCopy={handleCopy}
        busy={create.isPending || remove.isPending || copy.isPending}
      />

      <WeeklyScheduleEditor
        windows={windows.map((w) => ({
          id: w.id,
          weekday: w.weekday,
          start_minute: w.start_minute,
          end_minute: w.end_minute,
        }))}
        onAdd={(vars) => create.mutate(vars)}
        onRemove={(id) => setPendingRemove(id)}
        busy={create.isPending || remove.isPending || copy.isPending}
        profileTimezone={profileTimezone}
      />

      {windows.length > 0 && (
        <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
          <Alert tone="info">{t("schedules.notifications.hint")}</Alert>
          {windows.map((window) => (
            <WindowNotificationEditor key={window.id} scheduleId={scheduleId} window={window} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        title={t("schedules.windows.deleteTitle")}
        body={t("schedules.windows.deleteBody")}
        loading={remove.isPending}
        onCancel={() => setPendingRemove(null)}
        onConfirm={() => pendingRemove && remove.mutate(pendingRemove)}
      />
    </Card>
  );
}

function WindowNotificationEditor({ scheduleId, window }: { scheduleId: string; window: WorkScheduleWindow }) {
  const qc = useQueryClient();
  const [startTitle, setStartTitle] = useState(window.start_notification_title || "");
  const [startBody, setStartBody] = useState(window.start_notification_body || "");
  const [endTitle, setEndTitle] = useState(window.end_notification_title || "");
  const [endBody, setEndBody] = useState(window.end_notification_body || "");
  const save = useMutation({
    mutationFn: () => schedulesApi.patchWindow(scheduleId, window.id, {
      start_notification_title: startTitle,
      start_notification_body: startBody,
      end_notification_title: endTitle,
      end_notification_body: endBody,
    }),
    onSuccess: () => { void invalidatePolicyViews(qc); },
  });
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
      <strong style={{ gridColumn: "1 / -1", fontSize: 13 }}>{weekdayShort(window.weekday)} · {fmt(window.start_minute)}–{fmt(window.end_minute)}</strong>
      <FormField label={t("schedules.notifications.startTitle")}>{(id) => <Input id={id} value={startTitle} onChange={(e) => setStartTitle(e.target.value)} />}</FormField>
      <FormField label={t("schedules.notifications.startBody")}>{(id) => <Input id={id} value={startBody} onChange={(e) => setStartBody(e.target.value)} />}</FormField>
      <FormField label={t("schedules.notifications.endTitle")}>{(id) => <Input id={id} value={endTitle} onChange={(e) => setEndTitle(e.target.value)} />}</FormField>
      <FormField label={t("schedules.notifications.endBody")}>{(id) => <Input id={id} value={endBody} onChange={(e) => setEndBody(e.target.value)} />}</FormField>
      <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}><Button size="sm" variant="secondary" loading={save.isPending} onClick={() => save.mutate()}>{t("common.save")}</Button></div>
    </div>
  );
}
